import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Popup from '@atlaskit/popup';
import { IconButton } from '@atlaskit/button/new';
import HeadphonesIcon from '@atlaskit/icon/core/headphones';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import type { ActiveAudioContent } from '@confluence/audio';
import { AudioBriefingPopupContent, useAudioBriefingActions } from '@confluence/audio';
import { useSessionData } from '@confluence/session-data';

const i18n = defineMessages({
	listen: {
		id: 'object-sidebar-components.audio.button.tooltip',
		defaultMessage: 'Listen to briefing',
		description:
			'Message in tooltip which tells user that this button is tied to listen to a briefing',
	},
});

type AudioButtonProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
};

export const AudioButton: FC<AudioButtonProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
}) => {
	const { formatMessage, locale } = useIntl();

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

	const { initialize } = useAudioBriefingActions();

	const { cloudId, activationId } = useSessionData();

	const activeContent: ActiveAudioContent = {
		id: contentId,
		// Should we just fail if there is no contentType?
		contentType: contentType ?? 'page',
		spaceKey,
		contentTitle,
	};

	const handleClick = () => {
		setIsPopupOpen((prevIsPopupOpen) => !prevIsPopupOpen);
		initialize({
			activeContent,
			cloudId,
			activationId,
			locale,
		});
	};

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="left-end"
			content={() => <AudioBriefingPopupContent closePopup={() => setIsPopupOpen(false)} />}
			trigger={(triggerProps) => (
				<Tooltip content={formatMessage(i18n.listen)} position="left" hideTooltipOnClick>
					<IconButton
						{...triggerProps}
						label={formatMessage(i18n.listen)}
						appearance="subtle"
						icon={HeadphonesIcon}
						shape={fg('ai-smart-button-team-25') ? 'default' : 'circle'}
						onClick={handleClick}
						testId="audio-button"
					/>
				</Tooltip>
			)}
		/>
	);
};
